import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, ShoppingBag, PackageCheck, CheckCircle2, HelpCircle, Mail, Phone, MapPin, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const HowItWorks = () => {
  useEffect(() => { document.title = "How It Works · Dealswagon"; }, []);
  const steps = [
    { icon: ShoppingBag, title: "Shop", text: "Browse our latest deals and limited-quantity finds." },
    { icon: PackageCheck, title: "Order", text: "Add items to your cart and complete checkout." },
    { icon: Truck, title: "Pickup", text: "Choose drive-through pickup and collect your order from your vehicle." },
    { icon: CheckCircle2, title: "Enjoy", text: "Find it. Love it. Save Big." },
  ];
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="how-it-works-page">
      <div className="max-w-2xl">
        <div className="text-brand-red font-black uppercase tracking-widest text-xs">The Dealswagon Way</div>
        <h1 className="font-heading text-3xl sm:text-5xl text-brand-navy mt-1">How It Works</h1>
        <p className="text-slate-600 mt-3">Four simple steps between you and warehouse pricing.</p>
      </div>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {steps.map(({ icon: Icon, title, text }, i) => (
          <div key={title} className="rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-xl transition">
            <div className="text-brand-yellow font-heading text-3xl">{String(i + 1).padStart(2, "0")}</div>
            <Icon className="h-8 w-8 text-brand-red mt-2" />
            <div className="font-heading text-xl text-brand-navy mt-3">{title}</div>
            <p className="text-slate-600 text-sm mt-1">{text}</p>
          </div>
        ))}
      </div>
    </main>
  );
};

export const DriveThruPickup = () => {
  useEffect(() => { document.title = "Drive-Through Pickup · Dealswagon"; }, []);
  return (
    <main data-testid="pickup-page">
      <section className="relative overflow-hidden bg-brand-green text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative">
          <div className="inline-flex items-center gap-2 bg-white/15 border border-white/25 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest">
            <Truck className="h-4 w-4" /> Coming with Launch
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[0.95]">SHOP ONLINE.<br />DRIVE-THROUGH PICK UP.</h1>
          <p className="mt-4 max-w-xl text-white/90">Skip the store aisles. Pay online now, then drive to our Barrhaven pickup lane to collect your order. Fast, easy, contactless.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-heading text-2xl sm:text-3xl text-brand-navy mb-6">The 5-Step Flow</h2>
        <div className="grid md:grid-cols-5 gap-4">
          {["Browse our products online","Place your order","Pay online","Drive to our Barrhaven pickup lane","Collect your order"].map((s, i) => (
            <div key={i} className="rounded-2xl border border-brand-green/30 bg-brand-greenLight p-5">
              <div className="text-brand-greenDark font-heading text-3xl">{i + 1}</div>
              <div className="mt-2 font-bold text-slate-900">{s}</div>
            </div>
          ))}
        </div>
        <p className="text-slate-500 text-sm mt-6">Pickup dates, times, address and vehicle instructions will be published prior to opening.</p>
      </section>
    </main>
  );
};

export const About = () => {
  const [page, setPage] = useState(null);
  useEffect(() => { document.title = "About Us · Dealswagon"; api.get("/pages/about").then((r) => setPage(r.data)).catch(() => {}); }, []);
  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="about-page">
      <h1 className="font-heading text-3xl sm:text-5xl text-brand-navy">{page?.title || "About Us"}</h1>
      <div className="mt-6 text-slate-700 text-lg leading-relaxed whitespace-pre-line">{page?.content}</div>
    </main>
  );
};

export const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { document.title = "FAQ · Dealswagon"; api.get("/faqs").then((r) => setFaqs(r.data)); }, []);
  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="faq-page">
      <div className="inline-flex items-center gap-2 text-brand-red font-black uppercase tracking-widest text-xs">
        <HelpCircle className="h-3.5 w-3.5" /> Common Questions
      </div>
      <h1 className="font-heading text-3xl sm:text-5xl text-brand-navy mt-1">FAQ</h1>

      <div className="mt-8 space-y-2">
        {faqs.map((f, i) => (
          <div key={f.id} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
            <button data-testid={`faq-toggle-${i}`} onClick={() => setOpen(open === i ? null : i)}
              className="w-full text-left px-5 py-4 flex justify-between items-center hover:bg-slate-50">
              <span className="font-bold text-slate-900">{f.question}</span>
              <span className="text-brand-red font-black text-lg">{open === i ? "−" : "+"}</span>
            </button>
            {open === i && <div className="px-5 pb-5 text-slate-700 text-sm leading-relaxed">{f.answer}</div>}
          </div>
        ))}
      </div>
    </main>
  );
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [s, setS] = useState({});
  useEffect(() => { document.title = "Contact · Dealswagon"; api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try { await api.post("/contact", form); toast.success("Message sent — we'll be in touch."); setForm({ name: "", email: "", phone: "", message: "" }); }
    catch { toast.error("Could not send. Please try again."); }
    setSending(false);
  };
  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="contact-page">
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="text-brand-red font-black uppercase tracking-widest text-xs">Get in Touch</div>
          <h1 className="font-heading text-3xl sm:text-5xl text-brand-navy mt-1">Contact Us</h1>
          <p className="text-slate-600 mt-3">Have a question? Want to be notified when we open? Drop us a line.</p>
          <ul className="mt-6 space-y-3 text-slate-700 text-sm">
            <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-brand-red shrink-0" /> {s.business_address || "Barrhaven, Ottawa, ON"}</li>
            <li className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5 text-brand-red shrink-0" /> {s.business_phone || <span className="text-slate-400">Phone available at launch</span>}</li>
            <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 text-brand-red shrink-0" /> {s.business_email || "hello@dealswagon.ca"}</li>
            <li className="flex items-start gap-2 whitespace-pre-line"><Clock className="h-4 w-4 mt-0.5 text-brand-red shrink-0" /> {s.hours_of_operation || <span className="text-slate-400">Business hours announced soon</span>}</li>
          </ul>
        </div>
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3">
          <Field label="Name" value={form.name} required onChange={(v) => setForm({ ...form, name: v })} testId="contact-name" />
          <Field label="Email" type="email" value={form.email} required onChange={(v) => setForm({ ...form, email: v })} testId="contact-email" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contact-phone" />
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Message *</span>
            <textarea value={form.message} required onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="contact-message" rows={5}
              className="mt-1 w-full rounded-lg border border-slate-200 p-3 focus:outline-none focus:ring-2 focus:ring-brand-navy" />
          </label>
          <button type="submit" disabled={sending} data-testid="contact-submit"
            className="w-full bg-brand-red hover:bg-brand-redHover disabled:opacity-60 text-white font-black uppercase tracking-wider h-11 rounded-full">
            {sending ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </main>
  );
};

const Field = ({ label, value, onChange, type = "text", required, testId }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</span>
    <input type={type} value={value} required={required} data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 focus:outline-none focus:ring-2 focus:ring-brand-navy" />
  </label>
);

export const CMSPage = ({ slug, fallbackTitle }) => {
  const [page, setPage] = useState(null);
  useEffect(() => {
    document.title = `${fallbackTitle || "Page"} · Dealswagon`;
    api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setPage({ title: fallbackTitle, content: "This page is coming soon." }));
  }, [slug, fallbackTitle]);
  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid={`cms-page-${slug}`}>
      <h1 className="font-heading text-3xl sm:text-4xl text-brand-navy">{page?.title || fallbackTitle}</h1>
      <div className="mt-4 text-slate-700 whitespace-pre-line leading-relaxed">{page?.content}</div>
      <div className="mt-8">
        <Link to="/" className="text-brand-red font-bold text-sm hover:underline">← Back to home</Link>
      </div>
    </main>
  );
};

export const NotFound = () => (
  <main className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="not-found-page">
    <div className="font-heading text-8xl text-brand-red">404</div>
    <div className="font-heading text-2xl text-brand-navy mt-2">Page not found</div>
    <p className="text-slate-500 mt-2">The page you're looking for isn't in the warehouse.</p>
    <Link to="/" className="mt-6 inline-flex bg-brand-navy text-white rounded-full px-5 h-10 items-center font-bold">Go home</Link>
  </main>
);
